import type {
  ActingRepresentativesResponse,
  ClientActivityEvent,
  ClientActivityEventsResponse,
  ClientCall,
  ClientFile,
  ClientGroupMember,
  ClientMandateListResponse,
  ClientMandateOrdersResponse,
  ClientMeeting,
  ClientOrdersResponse,
  ClientParty,
  ClientRecurringOrdersResponse,
  SavingsPlanListResponse,
  UpdateClientRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { get } from "@/lib/api/_client";
import { getClientValueHistory } from "@/lib/api/client-accounts";
import { updateClient } from "@/lib/api/clients";
import { useInvalidateClientAccessData } from "@/lib/hooks/use-teams";

export function useClient(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", id],
    queryFn: () => get<any>(`/internal/clients/${id}`),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clients", id] });
  return { client: data ?? null, error, isLoading, mutate };
}

/**
 * After a client's own fields change (name, email, phone): refresh the detail
 * query plus the list and favourites, which show those fields. The per-section
 * subkeys (`["clients", id, ...]`) don't depend on them, so they are left alone.
 */
export function useInvalidateClient() {
  const queryClient = useQueryClient();
  return (clientId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["clients", clientId], exact: true }),
      queryClient.invalidateQueries({ queryKey: ["clients", "list"] }),
      queryClient.invalidateQueries({ queryKey: ["clients", "favourites"] }),
    ]);
}

/**
 * QL-604 — every client edit goes through here, so the refresh after a save is
 * the same everywhere. A name, owner, privacy or team change also moves team
 * client counts and the dashboard widgets, and a type change moves the
 * client's parties and group members, so those refresh too (every `["clients"]`
 * key), in the background: only a contact edit waits for the client to
 * refetch, because its caller reads the stored value straight back.
 * Resolves to the saved client: the server normalises the email, so read the
 * stored contact info from the result, not from what was typed.
 */
export function useUpdateClient() {
  const invalidateClient = useInvalidateClient();
  const invalidateClientAccessData = useInvalidateClientAccessData();
  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: UpdateClientRequest }) =>
      updateClient(clientId, data),
    onSuccess: async (_saved, { clientId, data }) => {
      const widerChange =
        data.name !== undefined ||
        data.type !== undefined ||
        data.ownerId !== undefined ||
        data.isPrivate !== undefined ||
        data.teamIds !== undefined;
      if (widerChange) {
        void invalidateClientAccessData();
        return;
      }
      await invalidateClient(clientId);
    },
  });
}

/**
 * QL-603 / QL-605 — save one contact field (email or phone) from a send-invite
 * form, leaving the other untouched. Those forms only offer Save for a
 * non-empty value: clearing a contact belongs on the client page, since
 * invites and the public-flow OTP use it. Resolves to the stored value, which
 * is what the field should show next.
 */
export function useSaveClientContact() {
  const updateClientMutation = useUpdateClient();
  return async (clientId: string, field: "email" | "mobilePhone", value: string) => {
    const saved = await updateClientMutation.mutateAsync({
      clientId,
      data: { [field]: value.trim() },
    });
    return saved[field] ?? "";
  };
}

export function useClientAccounts(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "accounts"],
    queryFn: () => get<any[]>(`/internal/client-accounts/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "accounts"] });
  return { accounts: data ?? [], error, isLoading, mutate };
}

// QL-356 — the Accounts tab's "Total value" chart. Keyed under "accounts" so
// every invalidation of the account list refreshes it too (prefix match).
export function useClientValueHistory(clientId: string | null) {
  const queryKey = ["clients", clientId, "accounts", "value-history"];
  const { data, error, isLoading } = useQuery({
    queryKey,
    queryFn: () => getClientValueHistory(clientId!),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey });
  return { history: data ?? null, error, isLoading, mutate };
}

// QL-150 step 4 — recurring orders sent to the bank at flow completion
// (read-only; bank updates status via the public callback). Empty for
// tenants without the recurring-orders gate — the tab hides itself then.
export function useClientRecurringOrders(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "recurring-orders"],
    queryFn: () =>
      get<ClientRecurringOrdersResponse>(`/internal/client-recurring-orders/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "recurring-orders"] });
  return { recurringOrders: data ?? { flowInstances: [] }, error, isLoading, mutate };
}

// QL-375 group 9 — mandate orders sent to the bank at flow completion
// (read-only; the bank updates status via the public callback). Empty for
// collections with mandates off — the tab hides itself then.
export function useClientMandateOrders(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "mandate-orders"],
    queryFn: () => get<ClientMandateOrdersResponse>(`/internal/client-mandate-orders/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "mandate-orders"] });
  return { mandateOrders: data ?? { flowInstances: [] }, error, isLoading, mutate };
}

// QL-150 — bank-synced savings plans (read-only; active plans only).
/** QL-375 — the client's bank-confirmed mandates (active only; placeholders never render). */
export function useClientMandates(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "mandates"],
    queryFn: () => get<ClientMandateListResponse>(`/internal/client-mandates/${clientId}`),
    enabled: !!clientId,
  });
  return { mandates: data?.data ?? [], error, isLoading };
}

export function useClientSavingsPlans(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "savings-plans"],
    queryFn: () => get<SavingsPlanListResponse>(`/internal/client-savings-plans/${clientId}`),
    enabled: !!clientId,
  });
  return { savingsPlans: data?.data ?? [], error, isLoading };
}

export function useClientNotes(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "notes"],
    queryFn: () => get<any[]>(`/internal/client-notes/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["clients", clientId, "notes"] }),
    [queryClient, clientId],
  );
  return { notes: data ?? [], error, isLoading, mutate };
}

// QP-184 — group composition (Group clients only; enabled-gate at call site).
export function useClientGroupMembers(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "group-members"],
    queryFn: () => get<ClientGroupMember[]>(`/internal/client-group-members/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  // Member changes (representative checkbox, removal) flip the resolved
  // acting representative, so invalidate that query too.
  const mutate = () => {
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "group-members"] });
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "acting-representatives"] });
    // QL-654 — who has to verify their identity changed with the people.
    queryClient.invalidateQueries({ queryKey: ["client-identity", clientId] });
    // QL-140 — the server recomputes the saved financial-situation capacity
    // block of every editable instance of this group, which moves the buffer
    // minimum and the completion verdict. The instance ids are not in scope
    // here, so invalidate the whole namespace: the financial-situation value
    // key, the completion key, and (QL-505 follow-up) the invite-recipient key
    // all live under it. Inactive queries are only marked stale, so this
    // costs nothing outside an open wizard.
    queryClient.invalidateQueries({ queryKey: ["flowInstances"] });
  };
  return { members: data ?? [], error, isLoading, mutate };
}

// QP-184 — related parties (representatives / POA / guardians), any client type.
export function useClientParties(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "parties"],
    queryFn: () => get<ClientParty[]>(`/internal/client-parties/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  // Party create/delete/expiry changes flip the resolved acting
  // representative (party wins over a group member), so invalidate it too.
  const mutate = () => {
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "parties"] });
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "acting-representatives"] });
    // QL-654 — the Verified ID summary lists the people who must verify; a party
    // added, edited or removed changes it (the cached single-person summary would
    // otherwise show a green badge on a new representative).
    queryClient.invalidateQueries({ queryKey: ["client-identity", clientId] });
    // QL-505 — a party create/update/delete can flip who answers the
    // product-assessment/quiz components (single answering subject vs. every
    // group member) and recomputes their stored verdicts, so every open flow
    // instance of this client must refetch.
    queryClient.invalidateQueries({ queryKey: ["flowInstances"] });
  };
  return { parties: data ?? [], error, isLoading, mutate };
}

// A STABLE empty list: `data?.representatives ?? []` was a fresh array on every
// render while there is no data (always for a Person, whose query is disabled),
// and effects that list `representatives` as a dependency re-ran each render and
// wiped what the advisor had typed.
const NO_REPRESENTATIVES: ActingRepresentativesResponse["representatives"] = [];

// QPBI-387 — the resolved acting representatives (parties win, Group falls
// back to its members flagged as representative), in order. QL-654 — there can
// be several. Group clients only; enabled-gate at call site.
export function useActingRepresentatives(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "acting-representatives"],
    queryFn: () =>
      get<ActingRepresentativesResponse>(
        `/internal/client-parties/${clientId}/acting-representatives`,
      ),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () => {
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "acting-representatives"] });
    queryClient.invalidateQueries({ queryKey: ["client-identity", clientId] });
  };
  return { representatives: data?.representatives ?? NO_REPRESENTATIVES, error, isLoading, mutate };
}

export function useClientCalls(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "calls"],
    queryFn: () => get<ClientCall[]>(`/internal/client-calls/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["clients", clientId, "calls"] }),
    [queryClient, clientId],
  );
  return { calls: data ?? [], error, isLoading, mutate };
}

export function useClientMeetings(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "meetings"],
    queryFn: () => get<ClientMeeting[]>(`/internal/client-meetings/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["clients", clientId, "meetings"] }),
    [queryClient, clientId],
  );
  return { meetings: data ?? [], error, isLoading, mutate };
}

export function useClientFiles(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "files"],
    queryFn: () => get<ClientFile[]>(`/internal/client-files/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["clients", clientId, "files"] }),
    [queryClient, clientId],
  );
  return { files: data ?? [], error, isLoading, mutate };
}

export function useClientFlowInstances(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "flowInstances"],
    queryFn: () => get<any[]>(`/internal/flow-instances/client/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({ queryKey: ["clients", clientId, "flowInstances"] });
  return { flowInstances: data ?? [], error, isLoading, mutate };
}

export function useClientOrders(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "orders"],
    queryFn: () => get<ClientOrdersResponse>(`/internal/client-orders/${clientId}`),
    enabled: !!clientId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["clients", clientId, "orders"] });
  return { orders: data ?? { flowInstances: [] }, error, isLoading, mutate };
}

export function useClientActivityEvents(clientId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["clients", clientId, "activityEvents"],
    queryFn: () =>
      get<ClientActivityEventsResponse>(`/internal/clients/${clientId}/activity-events`),
    enabled: !!clientId,
  });
  const events: ClientActivityEvent[] = data?.events ?? [];
  return { activityEvents: events, error, isLoading };
}
